// src/components/productPage/singleProductPageComponents/ysim/KsimLineChart.tsx

import { Line } from 'react-chartjs-2';
import { AxisScale, ChartDatasetWithAxis, LABELS } from './types';
import { buildYScale } from './axis';
import {
    axisTitleX,
    isFreqDomain,
    makeXTickFormatter,
    makeYTickFormatter,
    tooltipTitleText,
    siScale,
} from './formatters';
import 'chart.js/auto';

type Props = {
    plotDatasets: any; // Chart.js datasets (已選中的)
    allDatasets: ChartDatasetWithAxis[];
    selectedLabels: string[];
};

const noUnitLabels = new Set([LABELS.RIPPLE.toString(), 'Q Value']);

export default function KsimLineChart({ plotDatasets, allDatasets, selectedLabels }: Props) {
    const isFreq = isFreqDomain(selectedLabels);
    const xAxisTitle = axisTitleX(selectedLabels);
    const xTicks = makeXTickFormatter(isFreq);
    const yTicks = makeYTickFormatter(selectedLabels);

    const x: AxisScale = {
        type: isFreq ? 'logarithmic' : 'linear',
        title: { display: true, text: xAxisTitle },
        ticks: { ...xTicks, maxRotation: 60, minRotation: 45 },
        min: undefined,
        max: undefined,
    };

    const leftLabel = selectedLabels[0] ?? '';
    const rightLabel = selectedLabels[1];

    const leftDataset = allDatasets.find((d) => d.label === leftLabel);
    const leftMin = Math.min(...(leftDataset?.data?.map((p) => p?.y ?? 0) ?? []));

    const scales: { x: AxisScale; y: AxisScale; y1?: AxisScale } = {
        x,
        y: buildYScale(leftLabel, 'left', yTicks, isFinite(leftMin) ? leftMin : undefined),
    };

    // 若選兩條線，右側 y1
    if (rightLabel) {
        const rightDataset = allDatasets.find((d) => d.label === rightLabel);
        const rightMin = Math.min(...(rightDataset?.data?.map((p) => p?.y ?? 0) ?? []));
        // 設定 yAxisID
        const target = plotDatasets.datasets.find((d: any) => d.label === rightLabel);
        if (target) target.yAxisID = 'y1';
        scales.y1 = buildYScale(
            rightLabel,
            'right',
            yTicks,
            isFinite(rightMin) ? rightMin : undefined
        );
    } else {
        // 單線時，避免殘留 yAxisID
        plotDatasets.datasets.forEach((d: any) => (d.yAxisID = undefined));
    }

    const GetUnitRegex = /\(([^)]*)\)/;

    // console.log('selectedLabels', selectedLabels);
    // console.log('plotDatasets', plotDatasets);

    return (
        <Line
            data={plotDatasets}
            options={{
                maintainAspectRatio: false,
                responsive: true,
                scales,
                plugins: {
                    legend: { display: true, position: 'top' },
                    tooltip: {
                        mode: 'index',
                        callbacks: {
                            title: (items) => {
                                const x = items?.[0]?.parsed?.x;
                                if (x == null) return '';
                                return tooltipTitleText(selectedLabels, x);
                            },
                            label: (ctx) => {
                                const label = ctx.dataset.label || '';
                                const y = ctx.parsed.y;

                                if (y == null) return '';
                                let unit = label.match(GetUnitRegex)?.[1] ?? '%';
                                if (
                                    Array.isArray(selectedLabels) &&
                                    selectedLabels.length === 1 &&
                                    noUnitLabels.has(selectedLabels[0])
                                ) {
                                    unit = '';
                                }
                                const inPercent =
                                    selectedLabels.includes('Capacitance Change Vs Temperature') ||
                                    selectedLabels.includes('Capacitance Change Vs AC Voltage') ||
                                    selectedLabels.includes('Capacitance Change Vs Bias Voltage');

                                let valText = inPercent
                                    ? `${Number(y).toFixed(2)}%`
                                    : `${siScale(y)}${unit}`;

                                if (
                                    Array.isArray(selectedLabels) &&
                                    selectedLabels.length === 1 &&
                                    noUnitLabels.has(selectedLabels[0])
                                ) {
                                    valText = `${y.toFixed(2)}`;
                                }

                                return `${label}: ${valText}`;
                            },
                        },
                    },
                },
            }}
        />
    );
}
